import { Button, Loader } from "@screencloud/screencloud-ui-components";
import { FormattedMessage } from "react-intl";
import tvDisplay from "src/pages/Welcome/images/pairScreen/default-tv-display.svg";
import pixiDevice from "../NumberOfScreensStep/assets/pixi-device.svg";
import {
  LineItem,
  LineItemCaption,
  LineItemColumn,
  LineItemImage,
  LineItemImageWrap,
  LineItemPrice,
  LineItemTitle,
  OrderSummaryCardStyled,
  PurchaseError,
  PurchaseRow,
  SummaryDescription,
  SummaryDivider,
  SummaryHeader,
  SummaryHeading,
  SummaryItems,
  TotalRow,
  TotalValue,
} from "./styles";

export interface OrderSummaryProps {
  planName: string;
  licenses: number;
  licensesPrice: string;
  freeDevices: boolean;
  deviceCount: number;
  devicePrice: string;
  shippingPrice: string;
  total: string;
  purchasing: boolean;
  onPurchase: () => void;
  /** Charge/SCA failure message from usePurchase (useError); empty when none. */
  error?: string;
}

export const OrderSummary = ({
  planName,
  licenses,
  licensesPrice,
  freeDevices,
  deviceCount,
  devicePrice,
  shippingPrice,
  total,
  purchasing,
  onPurchase,
  error,
}: OrderSummaryProps) => (
  <OrderSummaryCardStyled data-testid="checkout-order-summary">
    <SummaryHeader>
      <SummaryHeading>
        <FormattedMessage
          id="pixi_wizard.checkout.summary.title"
          defaultMessage="Order Summary"
        />
      </SummaryHeading>
      <SummaryDescription>
        <FormattedMessage
          id="pixi_wizard.checkout.summary.description"
          defaultMessage="Review the items in your order and confirm your subscription details. Your total includes all selected licences, devices, discounts, and shipping charges."
        />
      </SummaryDescription>
    </SummaryHeader>

    <SummaryItems>
      <LineItem>
        <LineItemImageWrap>
          <LineItemImage src={tvDisplay} alt="" aria-hidden />
        </LineItemImageWrap>
        <LineItemColumn>
          <LineItemCaption>
            <FormattedMessage
              id="pixi_wizard.checkout.summary.plan_caption"
              defaultMessage="{plan} Plan"
              values={{ plan: planName }}
            />
          </LineItemCaption>
          <LineItemTitle data-testid="summary-licenses">
            <FormattedMessage
              id="pixi_wizard.checkout.summary.licenses"
              defaultMessage="x{count} Screen Licences"
              values={{ count: licenses }}
            />
          </LineItemTitle>
          <LineItemPrice>{licensesPrice}</LineItemPrice>
        </LineItemColumn>
      </LineItem>

      {freeDevices && (
        <LineItem data-testid="summary-devices">
          <LineItemImageWrap>
            <LineItemImage src={pixiDevice} alt="" aria-hidden />
          </LineItemImageWrap>
          <LineItemColumn>
            <LineItemTitle>
              <FormattedMessage
                id="pixi_wizard.checkout.summary.device"
                defaultMessage="x{count} PIXI device"
                values={{ count: deviceCount }}
              />
            </LineItemTitle>
            <LineItemPrice data-testid="summary-device-price">
              {devicePrice}
            </LineItemPrice>
            <LineItemPrice data-testid="summary-device-offer">
              <FormattedMessage
                id="pixi_wizard.checkout.summary.device_offer"
                defaultMessage="Free device: -{amount}"
                values={{ amount: devicePrice }}
              />
            </LineItemPrice>
            <LineItemPrice data-testid="summary-shipping">
              <FormattedMessage
                id="pixi_wizard.checkout.summary.shipping"
                defaultMessage="Shipping: +{amount}"
                values={{ amount: shippingPrice }}
              />
            </LineItemPrice>
          </LineItemColumn>
        </LineItem>
      )}

      <TotalRow>
        <LineItemColumn>
          <LineItemTitle>
            <FormattedMessage
              id="pixi_wizard.checkout.summary.total"
              defaultMessage="Total"
            />
          </LineItemTitle>
          <TotalValue data-testid="summary-total">{total}</TotalValue>
        </LineItemColumn>
      </TotalRow>
    </SummaryItems>

    <SummaryDivider />

    {error && (
      <PurchaseError data-testid="checkout-purchase-error">
        {error}
      </PurchaseError>
    )}

    <PurchaseRow>
      <Button
        primary
        onClick={onPurchase}
        disabled={purchasing}
        data-testid="checkout-purchase"
      >
        {purchasing ? (
          <Loader
            inline
            active
            size="small"
            data-testid="checkout-purchase-loader"
          />
        ) : (
          <FormattedMessage
            id="pixi_wizard.checkout.purchase"
            defaultMessage="Purchase"
          />
        )}
      </Button>
    </PurchaseRow>
  </OrderSummaryCardStyled>
);
